import type { BaseOptionType, DefaultOptionType } from 'antd/es/select';

export const POPUP_WIDTH_MAX = 600;
export const POPUP_WIDTH_PADDING = 32;
const POPUP_WIDTH_SAMPLE = 5;
/** Mirrors `.xm-item-option-detail` in _select.less: font-size 0.85em, margins 10px + 5px. */
const DETAIL_FONT_SCALE = 0.85;
const DETAIL_MARGIN = 15;

type Option = DefaultOptionType | BaseOptionType;

export type MeasureText = (text: string) => number;

/** Visible name of an option; a ReactNode label carries no measurable text, hence the fallbacks. */
export function optionText(option: Option): string {
  const { label, text, title, value } = option;
  if (typeof label === 'string') return label;
  if (typeof text === 'string') return text;
  if (typeof title === 'string') return title;
  return typeof value === 'string' ? value : '';
}

/** Secondary text floated after the name - a unit shortcut, a parent path. */
export function optionDetail(option: Option): string {
  return typeof option.detail === 'string' ? option.detail : '';
}

function flatten(options: readonly Option[]): Option[] {
  return options.flatMap(option => option.options?.length ? [option, ...flatten(option.options)] : [option]);
}

/** Details keyed by value - `labelRender` gets only the value, never the option. */
export function detailByValue(options: readonly Option[] | undefined, valueKey = 'value'): Map<unknown, string> {
  return new Map(flatten(options ?? [])
    .filter(option => optionDetail(option))
    .map(option => [option[valueKey], optionDetail(option)]));
}

function optionWidth(option: Option, measure: MeasureText): number {
  const detail = optionDetail(option);
  const detailWidth = detail ? measure(detail) * DETAIL_FONT_SCALE + DETAIL_MARGIN : 0;
  return measure(optionText(option)) + detailWidth;
}

/** Widest visible option, capped; only the longest few candidates are measured. */
export function estimatePopupWidth(options: readonly Option[] | undefined, measure: MeasureText): number | undefined {
  if (!options?.length) return;

  const candidates = flatten(options)
    .sort((a, b) => (optionText(b).length + optionDetail(b).length) - (optionText(a).length + optionDetail(a).length))
    .slice(0, POPUP_WIDTH_SAMPLE);

  const widest = Math.max(...candidates.map(option => optionWidth(option, measure)));
  // No measurable text at all: leave antd's own width behaviour in place.
  if (!widest) return;
  return Math.min(Math.ceil(widest) + POPUP_WIDTH_PADDING, POPUP_WIDTH_MAX);
}
